import React, { useEffect, useRef, useState } from "react";
import { useAppContext } from "../hooks/useAppContext";
import { Locale } from "../context/AppProvider";
import { useSearchParams } from "react-router-dom";
import { Transition } from "@headlessui/react";
import DropDownArrow from "./SVGIcons/DropDownArrow";
import { ExpandMoreThickIcon } from "@clovirtualfashion/components";

const SelectLocale = () => {
  const { brand, locale, setLocale } = useAppContext();
  const [searchParams, setSearchParams] = useSearchParams();
  const [open, setOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  // Close menu on click outside
  useEffect(() => {
    const closeLocaleMenu = (e: MouseEvent) => {
      if (
        menuRef.current instanceof HTMLDivElement &&
        !menuRef.current.contains(e.target as Node)
      ) {
        setOpen(false);
      }
    };
    document.body.addEventListener("click", closeLocaleMenu);
    return () => document.body.removeEventListener("click", closeLocaleMenu);
  }, []);

  const getLanguageName = (locale: string) => {
    if (Locale.Spanish === locale) {
      return "Español";
    } else if (Locale.French === locale) {
      return "Français (France)";
    } else if (Locale.Japanese === locale) {
      return "日本語";
    } else if (Locale.Korean === locale) {
      return "한국어";
    } else if (Locale.Portuguese === locale) {
      return "Português";
    } else if (Locale.Chinese === locale) {
      return "简体中文";
    } else {
      return "English";
    }
  };

  const updateQueryParams = (locale: Locale) => {
    const { search } = window.location;
    const searchParamsLocale = new URLSearchParams(search).get("brand");
    const newQueryParameters: URLSearchParams = new URLSearchParams();

    if (searchParamsLocale) {
      newQueryParameters.set("brand", brand.toLowerCase());
    }

    newQueryParameters.set("locale", locale);

    setSearchParams(newQueryParameters);
  };

  return (
    <div
      ref={menuRef}
      className="relative inline-flex h-7 w-40 cursor-pointer items-center justify-between gap-1.5 rounded-[300px] bg-grayscale-opacity-fixed-glevel2 px-3 font-bold"
      onClick={() => setOpen(!open)}
    >
      <span className="text-center text-xs font-medium leading-tight text-grayscale-solid-g10 md:flex md:items-center">
        {Object.entries(Locale).map(([key, value]) => {
          if (locale === value) {
            return getLanguageName(locale);
          }
        })}
      </span>
      <ExpandMoreThickIcon
        size={"xs4"}
        className={`transition-transform duration-[250ms] ${open ? "rotate-180" : ""}`}
        color={`${open ? "Blue/Solid/Blue6" : "Grayscale/Solid/G10"}`}
      />

      <Transition
        className="absolute right-0 top-[35px] z-40 block"
        show={open}
        as="div"
        appear={true}
        enter="transition duration-[250ms]"
        enterFrom="opacity-0"
        enterTo="opacity-100"
        leave="transition duration-[250ms]"
        leaveFrom="opacity-100"
        leaveTo="opacity-0"
      >
        <div
          className={`w-40 divide-y divide-gray-100 rounded-b-xl bg-grayscale-solid-elevate-level1 shadow-lg outline outline-1 outline-grayscale-solid-elevate-level2`}
          onMouseLeave={() => setOpen(false)}
        >
          <ul
            className="flex flex-col gap-0.5"
            aria-labelledby="dropdownDefaultButton"
          >
            {Object.entries(Locale).map(([key, value], index) => {
              let language = getLanguageName(value);
              return (
                <li
                  key={key}
                  className={`block cursor-pointer px-4 py-2 text-xs font-medium text-grayscale-solid-g10 hover:bg-grayscale-opacity-fixed-glevel2 ${index === Object.entries(Locale).length - 1 ? "rounded-b-xl" : ""} ${locale === value ? "bg-grayscale-opacity-fixed-glevel3 font-semibold" : ""}`}
                  onClick={() => {
                    setLocale(value);
                    updateQueryParams(value);
                    setOpen(false);
                  }}
                >
                  {language}
                </li>
              );
            })}
          </ul>
        </div>
      </Transition>
    </div>
  );
};

export default SelectLocale;
